import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Loader2, ChevronDown, Check, Feather, Compass } from "lucide-react";

export default function HelpRemember() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [visible, setVisible] = useState(5); // questions shown inside the expanded theme

  useEffect(() => {
    api.get("/questions/browse")
      .then(({ data }) => { setGroups(data); setOpen(data[0]?.period || null); })
      .finally(() => setLoading(false));
  }, []);

  const toggle = (period) => { setOpen(open === period ? null : period); setVisible(5); };
  const pick = (q) => navigate("/answer", { state: { question: { id: q.id, text: q.text } } });

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-2xl bg-coral/10 text-coral flex items-center justify-center shrink-0"><Compass size={24} /></span>
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Help Me Remember</h1>
          <p className="text-subtle mt-1">Not sure where to begin? Browse questions by chapter of life and answer whichever one stirs a memory. There's no wrong place to start.</p>
        </div>
      </div>

      <div data-testid="remember-reassurance" className="bg-sagelight border border-sage/30 rounded-2xl p-4">
        <p className="text-sm text-charcoal/85 leading-relaxed">
          <b className="text-teal">You don't need to answer every question.</b> Choose whichever one brings a memory to mind — a single answer is a lovely start.
        </p>
      </div>

      <div className="space-y-3">
        {groups.map((g, i) => {
          const isOpen = open === g.period;
          const answered = g.questions.filter((q) => q.answered).length;
          return (
            <div key={g.period} data-testid={`remember-group-${i}`} className="bg-white rounded-2xl border border-line overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <button data-testid={`remember-toggle-${i}`} onClick={() => toggle(g.period)}
                className="w-full flex items-center gap-4 p-5 text-left hover:bg-cream2/50 transition-colors">
                <span className="flex-1">
                  <span className="font-serif text-lg text-charcoal block">{g.period}</span>
                  <span className="block text-sm text-subtle">
                  {answered > 0
                    ? `${answered} answered · ${g.questions.length} questions to choose from`
                    : `${g.questions.length} questions to choose from`}
                </span>
                </span>
                <ChevronDown size={20} className={`text-subtle shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-3 pb-4 animate-fade-in">
                  {g.questions.slice(0, visible).map((q) => (
                    <button key={q.id} data-testid={`remember-q-${q.id}`} onClick={() => pick(q)}
                      className="w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-sagelight transition-colors">
                      <span className={`mt-0.5 shrink-0 ${q.answered ? "text-sage" : "text-line"}`}>{q.answered ? <Check size={17} /> : <Feather size={16} />}</span>
                      <span className={`flex-1 ${q.answered ? "text-subtle" : "text-charcoal"}`}>{q.text}</span>
                    </button>
                  ))}
                  <div className="flex items-center gap-4 px-3 pt-2">
                    {visible < g.questions.length && (
                      <button data-testid={`remember-more-${i}`} onClick={() => setVisible((v) => v + 5)}
                        className="text-sm font-semibold text-teal hover:underline">Show me more questions</button>
                    )}
                    {visible > 5 && (
                      <button data-testid={`remember-fewer-${i}`} onClick={() => setVisible(5)}
                        className="text-sm text-subtle hover:text-charcoal">Show fewer questions</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
